// Ad spend dialog — manual daily entry for Meta/TikTok/Google.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { listAdSpend, addAdSpend, deleteAdSpend } from "@/lib/analytics";
import { todayInRO } from "@/lib/format";

const CHANNELS = [
  { id: "meta", label: "Meta (Facebook + Instagram)" },
  { id: "tiktok", label: "TikTok Ads" },
  { id: "google", label: "Google Ads" },
  { id: "other", label: "Altele" },
];

export default function AdSpendDialog({ range, onChanged, trigger }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const today = todayInRO();
  const [form, setForm] = useState({ date: today, channel: "meta", amount: "", notes: "" });

  const reload = async () => {
    try {
      const r = await listAdSpend(range);
      setRows(r || []);
    } catch (e) { toast.error("Eroare la încărcare"); }
  };
  useEffect(() => { if (open) reload(); }, [open, JSON.stringify(range)]); // eslint-disable-line

  const add = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Suma invalidă"); return; }
    setBusy(true);
    try {
      await addAdSpend({ date: form.date, channel: form.channel, amount: Number(form.amount), notes: form.notes || null });
      toast.success("Înregistrat");
      setForm({ ...form, amount: "", notes: "" });
      await reload();
      onChanged?.();
    } catch (e) { toast.error("Eroare la salvare"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await deleteAdSpend(id);
      await reload();
      onChanged?.();
    } catch (e) { toast.error("Eroare"); }
  };

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Cheltuieli ads</DialogTitle>
          <DialogDescription>Introdu manual cheltuielile zilnice pentru fiecare canal. Integrarea automată Meta/TikTok/Google va veni ulterior.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-3">
              <label className="text-[11px] text-muted-foreground">Data</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="adspend-date" />
            </div>
            <div className="col-span-4">
              <label className="text-[11px] text-muted-foreground">Canal</label>
              <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                <SelectTrigger data-testid="adspend-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHANNELS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3">
              <label className="text-[11px] text-muted-foreground">Sumă (RON)</label>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="adspend-amount" />
            </div>
            <div className="col-span-2">
              <Button onClick={add} disabled={busy} className="w-full" data-testid="adspend-add">
                <Plus className="h-3.5 w-3.5 mr-1" /> Adaugă
              </Button>
            </div>
          </div>
          <Input placeholder="Note (opțional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

          <div className="rounded-lg border border-border max-h-[40vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Data</th>
                  <th className="text-left px-3 py-2">Canal</th>
                  <th className="text-right px-3 py-2">Sumă</th>
                  <th className="text-left px-3 py-2">Note</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan="5" className="px-3 py-6 text-center text-muted-foreground text-xs">Nicio cheltuială în interval</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-secondary/20">
                    <td className="px-3 py-2 tabular-nums text-xs">{(r.date || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 text-xs">{(CHANNELS.find((c) => c.id === r.channel)?.label) || r.channel}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{Number(r.amount).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.notes || "—"}</td>
                    <td className="text-right pr-2">
                      <button onClick={() => remove(r.id)} className="text-rose-500 hover:text-rose-400">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot className="bg-secondary/30">
                  <tr>
                    <td colSpan="2" className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground">Total</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{total.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON</td>
                    <td colSpan="2" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
